// The maturity ladder: the single source of truth for what each 1–5 score
// means. Used by the rating UI (chip labels + legend) and the report so the
// scale is defined the same way everywhere. Scored at the highest level whose
// criteria are fully met.

import type { NumericScore } from '../types/review';

export interface MaturityLevel {
  value: NumericScore;
  /** Short name shown on the score chip. */
  name: string;
  /** Full definition shown in the rating legend / report. */
  criteria: string;
}

export const MATURITY_LADDER: readonly MaturityLevel[] = [
  {
    value: 1,
    name: 'Absent',
    criteria: 'The mechanism or practice does not exist. No documentation, no evidence of practice.',
  },
  {
    value: 2,
    name: 'Ad hoc',
    criteria:
      'The practice occurs but depends on individuals. No current written standard, or the document does not match practice. Evidence is anecdotal: interviews, recollection.',
  },
  {
    value: 3,
    name: 'Defined',
    criteria:
      'A current written standard exists. Sampled cases show partial adherence (some pass, some fail), or full adherence whose evidence is collected manually or inconsistently.',
  },
  {
    value: 4,
    name: 'Operating',
    criteria:
      'The standard is followed consistently: all sampled cases adhere, exceptions are recorded and resolved. Evidence is systematic: logs, CI gates, registers, automated records, verifiable without interviews.',
  },
  {
    value: 5,
    name: 'Optimizing',
    criteria:
      'Level 4, plus the mechanism itself is measured and improving: trend data exists, and at least one documented improvement to the mechanism resulted from that data.',
  },
];

/** Lowest two rungs (Absent, Ad hoc) — a High-risk question here is a gap. */
export const GAP_SCORES: readonly NumericScore[] = [1, 2];

const NAME_BY_VALUE = new Map<NumericScore, string>(MATURITY_LADDER.map((l) => [l.value, l.name]));

/** Human label for a numeric score, e.g. 3 -> "Defined". */
export function maturityLevelName(score: NumericScore): string {
  return NAME_BY_VALUE.get(score) ?? String(score);
}
